import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import Chart from 'chart.js/auto';

@Component({
  standalone: false,
  selector: 'app-donut-chart',
  template: `
    <div [style.width.px]="view?.width || 400" style="position:relative">
      <canvas #chartCanvas></canvas>
      <div *ngIf="centerIcon" class="donut-center-icon">
        <i class="fa {{ centerIcon }}"></i>
      </div>
      <div *ngIf="chartOptions?.showLegend" class="donut-legend">
        <div *ngFor="let item of chartData" class="legend-item">
          <span class="legend-color" [style.background]="item.color"></span>
          <span class="legend-label">{{ item.name }}: {{ item.value }}</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .donut-center-icon {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
    .donut-center-icon i {
      font-size: 42px;
      color: #E81D25;
      opacity: 0.75;
      margin-bottom: 36px;
    }
    .donut-legend {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 12px;
      justify-content: center;
    }
    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
    }
    .legend-color {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      flex-shrink: 0;
    }
  `],
})
export class DonutChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() chartData: { name: string; value: number; color: string }[] = [];
  @Input() chartOptions: { showLegend?: boolean; legendTitle?: string } = {};
  @Input() view: { height?: number; width?: number; radius?: number; donutSize?: number } = {};
  @Input() centerIcon: string = '';

  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;

  private chart: any = null;
  private initialized = false;

  ngAfterViewInit() {
    this.createChart();
    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.initialized) {
      this.chart ? this.updateChart() : this.createChart();
    }
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }

  private getCutout(): string {
    const radius = this.view?.radius || 160;
    const donutSize = this.view?.donutSize || 40;
    return `${Math.round(((radius - donutSize) / radius) * 100)}%`;
  }

  private createChart() {
    if (!this.chartCanvas?.nativeElement) return;
    this.chart?.destroy();

    this.chart = new Chart(this.chartCanvas.nativeElement, {
      type: 'doughnut',
      data: {
        labels: this.chartData.map((d) => d.name),
        datasets: [
          {
            data: this.chartData.map((d) => d.value),
            backgroundColor: this.chartData.map((d) => d.color),
            borderWidth: 2,
          },
        ],
      },
      options: {
        cutout: this.getCutout(),
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
        },
      },
    });
  }

  private updateChart() {
    if (!this.chart) return;
    this.chart.data.labels = this.chartData.map((d) => d.name);
    this.chart.data.datasets[0].data = this.chartData.map((d) => d.value);
    (this.chart.data.datasets[0] as any).backgroundColor = this.chartData.map((d) => d.color);
    this.chart.update();
  }
}
